import { SplitFeatureFlag } from '@/utils/split/splitConfig';

import { useCallback } from 'react';
import { useSplitTreatments } from '@splitsoftware/splitio-react';

type Treatment = {
  treatment: string;
  config: string | null;
};

type EvaluateResult = {
  treatment: string;
  isFeatureFlagEnabled: boolean;
};

type FeatureFlagResult = {
  isReady: boolean;
  isFeatureFlagEnabled: boolean;
  treatment: string;
  config: unknown;
  evaluate: (key: string) => Promise<EvaluateResult>;
};

export const useSplitFeatureFlag = (
  flag: SplitFeatureFlag,
  attributes?: SplitIO.Attributes,
): FeatureFlagResult => {
  const { treatments, isReady, factory } = useSplitTreatments({
    names: [flag],
    attributes,
  });

  const treatment = treatments[flag] as Treatment;
  const isFeatureFlagEnabled = treatment?.treatment === 'on';

  const evaluate = useCallback(
    async (key: string): Promise<EvaluateResult> => {
      if (!factory) return { treatment: 'off', isFeatureFlagEnabled: false };
      const keyClient = factory.client(key);
      await keyClient.ready();
      const result = keyClient.getTreatment(flag);
      return {
        treatment: result,
        isFeatureFlagEnabled: result === 'on',
      };
    },
    [factory, flag],
  );

  return {
    isReady,
    isFeatureFlagEnabled,
    treatment: treatment?.treatment ?? 'off',
    config: treatment?.config ?? null,
    evaluate,
  };
};
